import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export interface DynamicCardFunctionality {
  isNameShown: boolean;
  isMobileNumberShown: boolean;
  isBranchShown: boolean;
  isStageShown: boolean;
  isStatusShown: boolean;
  isEmailShown: boolean;
  /**
   * Controls the next-up row on the card (follow-up / deadline chip). The
   * key was historically named `isCourseShown`; it now drives the follow-up
   * row on the dynamic card and the (still-named) "Course" column in the
   * dynamic table.
   */
  isFollowUpShown: boolean;
  /** Show aggregated payment status chip on the card footer. */
  isPaymentStatusShown: boolean;
  isLeadSourceShown: boolean;
  isCampaignShown: boolean;
  isSubstatusShown: boolean;
  isPriorityShown: boolean;
  isLastUpdatedOnShown: boolean;
  /** Labelled `createdAt` row/column — mirror of the lead slice. */
  isCreatedAtShown: boolean;
  isLastCallRecordShown: boolean;
  isUsersShown: boolean;
  isRemarksShown: boolean;
  isLeadScoreShown: boolean;
  /**
   * Per-tenant CustomInput ids the user wants displayed on each dynamic-card.
   * Mirrors the lead slice's shape — keys are stringified `customInputId`,
   * value `true` = show. Drives the `cfShow` query param on the stage-route
   * endpoint so the server can attach `cardCustomFields` to each row.
   */
  customFieldToggles: Record<string, boolean>;
  /**
   * Per-industry niche modules the user wants displayed on each dynamic-card
   * (e.g. "course", "university"). Keyed by `nicheModelName`. Drives the
   * `moduleShow` query param.
   */
  moduleToggles: Record<string, boolean>;
  /**
   * Tenant roles surfaced as an assignee column on each dynamic card/table/
   * board (e.g. show the Counsellor on the lead/deal). Keyed by stringified
   * roleId. Drives the `roleShow` query param so the server attaches
   * `cardRoles` (lead- + deal-level assignees) to each row.
   */
  roleToggles: Record<string, boolean>;
  /**
   * FAVORITE VIEW routes only: fingerprint of the view's saved column layout
   * that was last applied here (see `seedDynamicCardLayoutFromView` and
   * `favoriteViews/utils/viewColumns.ts`). Lets the view's definition win
   * ONCE per change, while the user's own toggles on the view survive until
   * the view is updated again. Absent on stage routes.
   */
  seededFromView?: string;
  /**
   * List view: the user's drag-and-drop column arrangement for THIS route,
   * as the ordered list of column keys last dropped (mirror of the lead
   * slice; see `leads/utils/listColumnOrder.ts`). Absent = default layout.
   */
  listColumnOrder?: string[];
  /** Header-menu state for THIS route (mirror of the lead slice). */
  listColumnFilters?: Record<string, string[]>;
  listColumnSort?: { key: string; dir: "asc" | "desc" } | null;
  /** List view: column widths in px the user dragged, by column key. */
  listColumnWidths?: Record<string, number>;
  /** List view: FROZEN (pinned) column keys — see the lead-card slice. */
  listColumnPins?: string[];
}

// Mirrors leadCardFunctionalitySlice defaults so every stage route
// (Qualify, Pre-Application, etc.) starts with the same populated grid.
// Payment status / last call record / lead score stay off by default.
const getInitialRouteState = (): DynamicCardFunctionality => ({
  isNameShown: true,
  isMobileNumberShown: true,
  isBranchShown: true,
  isUsersShown: true,
  isStageShown: true,
  isStatusShown: true,
  isEmailShown: true,
  isFollowUpShown: true,
  isLeadSourceShown: true,
  isCampaignShown: true,
  isSubstatusShown: true,
  isPriorityShown: true,
  isLastUpdatedOnShown: true,
  isRemarksShown: true,
  isPaymentStatusShown: false,
  isLastCallRecordShown: false,
  isLeadScoreShown: false,
  isCreatedAtShown: false,
  customFieldToggles: {},
  moduleToggles: {},
  roleToggles: {},
});

type DynamicCardFunctionalityState = Record<
  string | number,
  DynamicCardFunctionality
>;

const initialState: DynamicCardFunctionalityState = {};

const dynamicCardFunctionalitySlice = createSlice({
  name: "dynamicCardFunctionality",
  initialState,
  reducers: {
    initializeRoute: (state, action: PayloadAction<string | number>) => {
      const routeId = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
    },
    initializeRoutes: (state, action: PayloadAction<(string | number)[]>) => {
      action.payload.forEach((routeId) => {
        if (!state[routeId]) {
          state[routeId] = getInitialRouteState();
        }
      });
    },
    setDynamicStatusToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isStatusShown = value;
    },
    setDynamicEmailToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isEmailShown = value;
    },
    setDynamicFollowUpToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isFollowUpShown = value;
    },
    setDynamicPaymentStatusToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isPaymentStatusShown = value;
    },
    setDynamicLeadSourceToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isLeadSourceShown = value;
    },
    setDynamicCampaignToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isCampaignShown = value;
    },
    setDynamicSubstatusToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isSubstatusShown = value;
    },
    setDynamicPriorityToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isPriorityShown = value;
    },
    setDynamicLastUpdatedOnToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isLastUpdatedOnShown = value;
    },
    setDynamicCreatedAtToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isCreatedAtShown = value;
    },
    setDynamicLastCallRecordToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isLastCallRecordShown = value;
    },
    setDynamicUsersToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isUsersShown = value;
    },
    setDynamicRemarksToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isRemarksShown = value;
    },
    setDynamicLeadScoreToggle: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      state[routeId].isLeadScoreShown = value;
    },
    toggleDynamicCustomField: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        id: number | string;
        on: boolean;
      }>
    ) => {
      const { routeId, id, on } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      // Pre-existing per-route states (from before this field existed) may
      // be missing the toggle maps after a redux-persist rehydrate — guard
      // here so the very first dispatch doesn't blow up.
      if (!state[routeId].customFieldToggles) {
        state[routeId].customFieldToggles = {};
      }
      const key = String(id);
      if (on) {
        state[routeId].customFieldToggles[key] = true;
      } else {
        delete state[routeId].customFieldToggles[key];
      }
    },
    toggleDynamicModule: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        name: string;
        on: boolean;
      }>
    ) => {
      const { routeId, name, on } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      if (!state[routeId].moduleToggles) {
        state[routeId].moduleToggles = {};
      }
      if (on) {
        state[routeId].moduleToggles[name] = true;
      } else {
        delete state[routeId].moduleToggles[name];
      }
    },
    toggleDynamicRole: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        id: number | string;
        on: boolean;
      }>
    ) => {
      const { routeId, id, on } = action.payload;
      if (!state[routeId]) {
        state[routeId] = getInitialRouteState();
      }
      if (!state[routeId].roleToggles) {
        state[routeId].roleToggles = {};
      }
      const key = String(id);
      if (on) {
        state[routeId].roleToggles[key] = true;
      } else {
        delete state[routeId].roleToggles[key];
      }
    },
    /** List view header drag-and-drop on a stage route / view. */
    setDynamicListColumnOrder: (
      state,
      action: PayloadAction<{ routeId: string | number; order: string[] }>,
    ) => {
      const { routeId, order } = action.payload;
      if (!state[routeId]) state[routeId] = getInitialRouteState();
      state[routeId].listColumnOrder = order;
    },
    setDynamicListColumnFilter: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        key: string;
        selected: string[] | null;
      }>,
    ) => {
      const { routeId, key, selected } = action.payload;
      if (!state[routeId]) state[routeId] = getInitialRouteState();
      const next = { ...(state[routeId].listColumnFilters ?? {}) };
      if (selected) next[key] = selected;
      else delete next[key];
      state[routeId].listColumnFilters = Object.keys(next).length ? next : undefined;
    },
    setDynamicListColumnSort: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        sort: { key: string; dir: "asc" | "desc" } | null;
      }>,
    ) => {
      const { routeId, sort } = action.payload;
      if (!state[routeId]) state[routeId] = getInitialRouteState();
      state[routeId].listColumnSort = sort;
    },
    setDynamicListColumnWidth: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        key: string;
        width: number | null;
      }>,
    ) => {
      const { routeId, key, width } = action.payload;
      if (!state[routeId]) state[routeId] = getInitialRouteState();
      const next = { ...(state[routeId].listColumnWidths ?? {}) };
      if (width == null) delete next[key];
      else next[key] = Math.round(width);
      state[routeId].listColumnWidths = Object.keys(next).length ? next : undefined;
    },
    toggleDynamicListColumnPin: (
      state,
      action: PayloadAction<{ routeId: string | number; key: string }>,
    ) => {
      const { routeId, key } = action.payload;
      if (!state[routeId]) state[routeId] = getInitialRouteState();
      const pins = state[routeId].listColumnPins ?? [];
      const next = pins.includes(key)
        ? pins.filter((k) => k !== key)
        : [...pins, key];
      state[routeId].listColumnPins = next.length ? next : undefined;
    },
    clearDynamicListColumnFilters: (
      state,
      action: PayloadAction<string | number>,
    ) => {
      const route = state[action.payload];
      if (!route) return;
      route.listColumnFilters = undefined;
      route.listColumnSort = null;
    },
    /**
     * Apply a FAVORITE VIEW's saved column layout to its route. Replaces the
     * entry wholesale (defaults underneath, so a key the view never saved
     * falls back to the default) and stamps `seededFromView` so the same
     * definition is never re-applied over the user's later toggles.
     */
    seedDynamicCardLayoutFromView: (
      state,
      action: PayloadAction<{
        routeId: string | number;
        layout: Record<string, unknown>;
        seedKey: string;
      }>
    ) => {
      const { routeId, layout, seedKey } = action.payload;
      const base = getInitialRouteState();
      const next: Record<string, unknown> = { ...base };
      for (const [key, defaultValue] of Object.entries(base)) {
        const value = layout?.[key];
        if (typeof defaultValue === "boolean") {
          if (typeof value === "boolean") next[key] = value;
        } else if (value && typeof value === "object" && !Array.isArray(value)) {
          const map: Record<string, boolean> = {};
          for (const [id, on] of Object.entries(value as Record<string, unknown>)) {
            if (on) map[id] = true;
          }
          next[key] = map;
        }
      }
      next.seededFromView = seedKey;
      // A view decides WHICH columns show; the header arrangement stays the
      // user's own, so keep it across a re-seed.
      const prior = state[routeId];
      if (prior?.listColumnOrder?.length) next.listColumnOrder = prior.listColumnOrder;
      if (prior?.listColumnFilters) next.listColumnFilters = prior.listColumnFilters;
      if (prior?.listColumnSort) next.listColumnSort = prior.listColumnSort;
      if (prior?.listColumnWidths) next.listColumnWidths = prior.listColumnWidths;
      if (prior?.listColumnPins?.length) next.listColumnPins = prior.listColumnPins;
      state[routeId] = next as unknown as DynamicCardFunctionality;
    },
    /**
     * "Reset Layouts" on a stage route / view — back to the WORKSPACE layout
     * for this route when the tenant has one, else the factory defaults.
     */
    resetDynamicCardLayout: (
      state,
      action: PayloadAction<
        string | number | { routeId: string | number; workspace?: unknown }
      >,
    ) => {
      const payload = action.payload;
      const routeId =
        typeof payload === "object" && payload !== null && "routeId" in payload
          ? payload.routeId
          : (payload as string | number);
      const workspace =
        typeof payload === "object" && payload !== null && "workspace" in payload
          ? payload.workspace
          : undefined;
      state[routeId] =
        workspace && typeof workspace === "object"
          ? {
              ...getInitialRouteState(),
              ...(workspace as Partial<DynamicCardFunctionality>),
            }
          : getInitialRouteState();
    },
    /**
     * Replace the per-route layout map with the user's DB-saved blob on boot.
     * Each route is merged over `getInitialRouteState()` so missing keys fall
     * back to defaults. Dispatched once, after auth verify; the write-through
     * sync middleware ignores this action so hydrating never echoes a save.
     */
    hydrateDynamicCardLayout: (_state, action: PayloadAction<unknown>) => {
      const payload = action.payload;
      if (!payload || typeof payload !== "object") return {};
      const next: DynamicCardFunctionalityState = {};
      const map = payload as Record<string, Partial<DynamicCardFunctionality>>;
      for (const [routeId, route] of Object.entries(map)) {
        next[routeId] = { ...getInitialRouteState(), ...(route ?? {}) };
      }
      return next;
    },
  },
});

export const {
  initializeRoute,
  initializeRoutes,
  setDynamicStatusToggle,
  setDynamicEmailToggle,
  setDynamicFollowUpToggle,
  setDynamicPaymentStatusToggle,
  setDynamicLeadSourceToggle,
  setDynamicCampaignToggle,
  setDynamicSubstatusToggle,
  setDynamicPriorityToggle,
  setDynamicLastUpdatedOnToggle,
  setDynamicCreatedAtToggle,
  setDynamicLastCallRecordToggle,
  setDynamicUsersToggle,
  setDynamicRemarksToggle,
  setDynamicLeadScoreToggle,
  toggleDynamicCustomField,
  toggleDynamicModule,
  toggleDynamicRole,
  setDynamicListColumnOrder,
  setDynamicListColumnFilter,
  setDynamicListColumnSort,
  clearDynamicListColumnFilters,
  setDynamicListColumnWidth,
  toggleDynamicListColumnPin,
  seedDynamicCardLayoutFromView,
  resetDynamicCardLayout,
  hydrateDynamicCardLayout,
} = dynamicCardFunctionalitySlice.actions;

export default dynamicCardFunctionalitySlice.reducer;
